import React, { useId } from 'react';
import './WellwiredVisuals.css';

const roomScenes = [
  { name: 'dawn', image: 'bedroom-dawn' },
  { name: 'midday', image: 'bedroom-midday' },
  { name: 'dusk', image: 'bedroom-dusk' },
  { name: 'evening', image: 'bedroom-evening' },
  { name: 'night', image: 'bedroom-night-path-upper-lip' },
];

// User-supplied AI concepts, explicitly captioned in the consuming page.
// Each moment has its own image; keep all scenes mounted for a smooth dissolve.
export function RoomStudy({ moment = 0, className = '', hero = false }) {
  return (
    <div className={`ww-room-study ${className}`} data-moment={moment} aria-hidden="true">
      {hero ? (
        <img
          className="ww-room-hero-image"
          src="/images/wellwired/bedroom-evening.webp"
          srcSet="/images/wellwired/bedroom-evening-640.webp 640w, /images/wellwired/bedroom-evening.webp 1280w"
          sizes="(max-width: 760px) 90vw, 45vw"
          width="1280"
          height="853"
          alt=""
          loading="eager"
          decoding="async"
        />
      ) : roomScenes.map((scene, index) => (
        <img
          key={scene.name}
          className={`ww-room-scene${index === moment ? ' ww-room-scene-active' : ''}`}
          data-scene={scene.name}
          src={`/images/wellwired/${scene.image}.webp`}
          srcSet={`/images/wellwired/${scene.image}-640.webp 640w, /images/wellwired/${scene.image}.webp 1280w`}
          sizes="(max-width: 760px) 90vw, 55vw"
          width="1280"
          height="853"
          alt=""
          loading="lazy"
          decoding="async"
        />
      ))}
    </div>
  );
}

export function PillarStudy({ name }) {
  const id = useId().replace(/:/g, '');
  return (
    <div className={`ww-pillar-study ww-study-${name.toLowerCase()}`} aria-hidden="true">
      <svg viewBox="0 0 600 420" preserveAspectRatio="xMidYMid slice" fill="none" focusable="false">
        <defs>
          <linearGradient id={`${id}-light`} x1="150" y1="70" x2="540" y2="400" gradientUnits="userSpaceOnUse"><stop stopColor="#fff8e3" /><stop offset="1" stopColor="#e1c5a0" stopOpacity="0" /></linearGradient>
          <linearGradient id={`${id}-fold`}><stop stopColor="#c8c6b9" /><stop offset=".42" stopColor="#f8f6ed" /><stop offset=".8" stopColor="#e3e0d3" /><stop offset="1" stopColor="#b9bdaf" /></linearGradient>
          <linearGradient id={`${id}-stone`} x2="0" y2="1"><stop stopColor="#ddd9ce" /><stop offset="1" stopColor="#aaa99e" /></linearGradient>
          <radialGradient id={`${id}-pool`}><stop stopColor="#e7ebe5" /><stop offset="1" stopColor="#a5b3ad" /></radialGradient>
          <radialGradient id={`${id}-amber`}><stop stopColor="#d8a365" stopOpacity=".3" /><stop offset="1" stopColor="#d8a365" stopOpacity="0" /></radialGradient>
        </defs>
        {name === 'Light' && <>
          <path d="M0 0H600V420H0Z" fill="#e5d6bf" />
          <path d="M0 318H600V420H0Z" fill="#d6b992" />
          <path d="M139 55H287V287H139Z" fill="#b49875" />
          <path d="M152 55H287V276L152 289Z" fill="#fff1ce" />
          <path d="M152 289L287 276L600 366L362 420Z" fill={`url(#${id}-light)`} />
          <path d="M209 55V283" stroke="#e1ceb0" strokeWidth="5" />
          <path d="M208 283L213 283L475 397L465 399Z" fill="#b79a73" opacity=".3" />
          <path d="M0 318H138M287 318H600" stroke="#bca17e" opacity=".3" />
          <path d="M287 50V288L304 310" stroke="#f8ecd8" opacity=".5" />
        </>}
        {name === 'Signal' && <>
          <path d="M0 0H600V420H0Z" fill="#1d2a2e" />
          <circle cx="395" cy="240" r="200" fill={`url(#${id}-amber)`} />
          <path d="M130 55L395 102V327L130 367Z" fill="#253439" />
          <path d="M130 55L145 49L411 95L395 102Z" fill="#3c4a4b" />
          <path d="M395 102L411 95V318L395 327Z" fill="#131e23" />
          <path d="M0 245H190Q208 245 208 227V151Q208 133 226 137L316 153Q331 155 331 172V260Q331 278 349 276L600 239" stroke="#d3a875" strokeWidth="2" />
          <path d="M0 251H185Q202 251 202 234V158" stroke="#d3a875" opacity=".22" />
          <circle cx="208" cy="185" r="4" fill="#e8c296" />
          <circle cx="331" cy="228" r="4" fill="#e8c296" />
          <path d="M130 367L395 327L467 350L220 400Z" fill="#131e23" opacity=".4" />
        </>}
        {name === 'Air' && <>
          <path d="M0 0H600V420H0Z" fill="#e2e5dc" />
          <path d="M77 0H141V420H77Z" fill="#f9f8ed" opacity=".6" />
          {[0, 1, 2, 3, 4].map(n => <path key={n} d={`M${125 + n * 74} -20C${92 + n * 74} 104 ${218 + n * 52} 174 ${158 + n * 73} 300Q${129 + n * 73} 378 ${165 + n * 73} 440H${240 + n * 73}Q${204 + n * 73} 378 ${233 + n * 73} 300C${293 + n * 52} 174 ${167 + n * 74} 104 ${200 + n * 74} -20Z`} fill={`url(#${id}-fold)`} />)}
        </>}
        {name === 'Water' && <>
          <path d="M0 0H600V420H0Z" fill={`url(#${id}-pool)`} />
          {[1, 2, 3, 4, 5, 6, 7].map(n => <g key={n}>
            <ellipse cx="335" cy="230" rx={n * 37} ry={n * 18} stroke="#6f8981" strokeWidth="1.1" opacity={.55 - n * .045} />
            <ellipse cx="335" cy="228" rx={n * 37 + 2} ry={n * 18} stroke="#f5f6e9" opacity={.7 - n * .06} />
          </g>)}
          <path d="M0 0H600L0 126Z" fill="#eeece2" />
          <path d="M0 126L600 0V16L0 145Z" fill="#a6a99b" />
          <ellipse cx="335" cy="230" rx="5" ry="2" fill="#f5f6e9" />
        </>}
        {name === 'Quiet' && <>
          <path d="M0 0H600V420H0Z" fill="#e4ddd0" />
          <path d="M396 0H600V420H396Z" fill="#d8cdbb" />
          <path d="M0 335L396 310L600 349V420H0Z" fill="#d0c2ad" />
          <path d="M396 0V310L600 349" stroke="#b9ac97" opacity=".5" />
          <path d="M178 316L385 305L495 360L290 395Z" fill="#a99b87" opacity=".3" />
          <path d="M173 264Q173 252 189 252H340Q355 252 355 264V319Q355 333 338 333H190Q173 333 173 319Z" fill={`url(#${id}-stone)`} />
          <ellipse cx="264" cy="253" rx="91" ry="20" fill="#e8e3d8" />
          <path d="M235 227V181Q235 164 253 164H275Q294 164 294 181V227Q294 236 264 236Q235 236 235 227Z" fill="#bba88c" />
          <ellipse cx="264" cy="164" rx="11" ry="3" fill="#8f7f68" />
          <path d="M264 165C261 140 274 112 286 99M272 133Q299 128 299 110M268 146Q246 134 249 119" stroke="#766c52" strokeWidth="1.5" />
        </>}
      </svg>
    </div>
  );
}

export function BlueprintStudy() {
  return (
    <div className="ww-blueprint-study" aria-hidden="true">
      <svg viewBox="0 0 600 380" fill="none" focusable="false">
        <path d="M102 64L474 31L510 324L138 358Z" fill="#d8cbb7" opacity=".6" />
        <path d="M78 42L458 21L480 321L100 344Z" fill="#efe5d5" />
        <g transform="translate(122 71) rotate(-3)">
          <path d="M0 0H296V206H0Z" stroke="#6a746f" strokeWidth="3" />
          <path d="M119 0V124H296M0 124H75M119 162V206M200 124V206" stroke="#6a746f" strokeWidth="2" />
          <path d="M22 25H95V91H22ZM218 25H274V91H218Z" stroke="#a8a79a" />
          <path d="M27 30H53V47H27ZM62 30H88V47H62ZM223 30H244V47H223ZM250 30H270V47H250Z" stroke="#a8a79a" />
          <path d="M143 25H176V93H143ZM11 150H65V182H11ZM224 146H278V184H224Z" stroke="#a8a79a" />
          <path d="M75 124V162A38 38 0 0 0 113 124M119 162H157A38 38 0 0 0 119 124" stroke="#a8a79a" />
          <path d="M57 61V109H184V165H249M184 109H247V61M57 109V166H40" stroke="#ac804b" strokeDasharray="3 4" />
          {[ [57,61], [247,61], [184,165], [40,166], [249,165] ].map(([x,y]) => <circle key={`${x}-${y}`} cx={x} cy={y} r="5" fill="#efe5d5" stroke="#ac804b" />)}
          <path d="M0 -18H296M-18 0V206M0 -23V-13M296 -23V-13M-23 0H-13M-23 206H-13M0 233H296" stroke="#aaa798" strokeWidth=".7" />
          <path d="M0 251H55M0 258H35M239 250H296" stroke="#6a746f" />
        </g>
      </svg>
      <span>One coordinated Blueprint · illustrative drawing</span>
    </div>
  );
}
